import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Rating,
  Divider,
} from "@mui/material";
import {
  Close,
  Phone,
  Email,
  ShoppingCart,
  Star,
  StarBorder,
  ArrowForward,
  CheckCircle,
  School,
  MedicalServices,
  TheaterComedy,
  AttachMoney,
  SportsBasketball,
  Computer,
  Restaurant,
  LocalHospital,
} from "@mui/icons-material";
import styles from "./Innovation.module.css";

// App Categories Data
const categories = [
  { id: "all", label: "All Apps", icon: "📱" },
  { id: "education", label: "Education", icon: "🎓" },
  { id: "health", label: "Health", icon: "🏥" },
  { id: "media", label: "Media & Entertainment", icon: "🎬" },
  { id: "finance", label: "Finance", icon: "💰" },
  { id: "sports", label: "Sports & Leisure", icon: "⚽" },
];

// Apps Data
const appsData = [
  {
    id: 1,
    title: "EduLearn Pro",
    category: "education",
    description:
      "Interactive learning platform with AI-powered tutoring, live classes, and progress tracking for students of all ages.",
    image:
      "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=600&h=400&fit=crop",
    icon: <School />,
    price: "£49.99",
    rating: 4.8,
    reviews: 234,
    features: [
      "AI Tutoring",
      "Live Classes",
      "Progress Tracking",
      "Interactive Quizzes",
    ],
    sector: "Education",
  },
  {
    id: 2,
    title: "MediTrack Health",
    category: "health",
    description:
      "Comprehensive health monitoring app with patient records, appointment scheduling, and telemedicine features.",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop",
    icon: <MedicalServices />,
    price: "£79.99",
    rating: 4.9,
    reviews: 412,
    features: [
      "Patient Records",
      "Telemedicine",
      "Appointment Scheduling",
      "Health Analytics",
    ],
    sector: "Health",
  },
  {
    id: 3,
    title: "MediaStream Pro",
    category: "media",
    description:
      "Advanced media streaming and content management platform for creators, publishers, and entertainment companies.",
    image:
      "https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?w=600&h=400&fit=crop",
    icon: <TheaterComedy />,
    price: "£59.99",
    rating: 4.7,
    reviews: 189,
    features: [
      "Content Management",
      "Live Streaming",
      "Analytics Dashboard",
      "Monetization",
    ],
    sector: "Media & Entertainment",
  },
  {
    id: 4,
    title: "FinSmart Banking",
    category: "finance",
    description:
      "Digital banking and financial management app with real-time transactions, investment tracking, and AI insights.",
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop",
    icon: <AttachMoney />,
    price: "£69.99",
    rating: 4.6,
    reviews: 356,
    features: [
      "Real-time Transactions",
      "Investment Tracking",
      "AI Insights",
      "Budget Planning",
    ],
    sector: "Finance",
  },
  {
    id: 5,
    title: "FitTrack Sports",
    category: "sports",
    description:
      "Sports performance tracking app with real-time analytics, workout plans, and community challenges.",
    image:
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop",
    icon: <SportsBasketball />,
    price: "£39.99",
    rating: 4.5,
    reviews: 567,
    features: [
      "Performance Analytics",
      "Workout Plans",
      "Community Challenges",
      "GPS Tracking",
    ],
    sector: "Sports & Leisure",
  },
  {
    id: 6,
    title: "CodeStudio IDE",
    category: "education",
    description:
      "Cloud-based development environment with collaboration tools, code review, and deployment features for teams.",
    image:
      "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&h=400&fit=crop",
    icon: <Computer />,
    price: "£89.99",
    rating: 4.8,
    reviews: 278,
    features: [
      "Cloud IDE",
      "Team Collaboration",
      "Code Review",
      "Deployment Tools",
    ],
    sector: "Education",
  },
  {
    id: 7,
    title: "RestoManager POS",
    category: "media",
    description:
      "Complete restaurant management system with inventory control, order management, and customer analytics.",
    image:
      "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&h=400&fit=crop",
    icon: <Restaurant />,
    price: "£54.99",
    rating: 4.4,
    reviews: 198,
    features: [
      "Inventory Control",
      "Order Management",
      "Customer Analytics",
      "Staff Management",
    ],
    sector: "Media & Entertainment",
  },
  {
    id: 8,
    title: "HealthConnect",
    category: "health",
    description:
      "Patient-doctor communication platform with remote monitoring, prescription management, and health records.",
    image:
      "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=600&h=400&fit=crop",
    icon: <LocalHospital />,
    price: "£99.99",
    rating: 4.9,
    reviews: 445,
    features: [
      "Remote Monitoring",
      "Prescription Management",
      "Health Records",
      "Video Consultations",
    ],
    sector: "Health",
  },
];

// Sector Icons
const sectorIcons = {
  Education: <School />,
  Health: <MedicalServices />,
  "Media & Entertainment": <TheaterComedy />,
  Finance: <AttachMoney />,
  "Sports & Leisure": <SportsBasketball />,
};

const Innovation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedApp, setSelectedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Filter apps based on selected category
  const filteredApps =
    selectedCategory === "all"
      ? appsData
      : appsData.filter((app) => app.category === selectedCategory);

  // Get unique sectors from apps
  const sectors = [...new Set(appsData.map((app) => app.sector))];

  const handleOpenDialog = (app) => {
    setSelectedApp(app);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedApp(null);
  };

  return (
    <Box className={styles.innovationPage}>
      {/* Hero Section */}
      {/* ===== HERO SECTION WITH BACKGROUND IMAGE ===== */}
      <Box className={styles.heroSection}>
        {/* Animated Background Elements */}
        <Box className={styles.animatedBg}>
          {/* Floating Particles */}
          <Box className={`${styles.particle} ${styles.particle1}`}></Box>
          <Box className={`${styles.particle} ${styles.particle2}`}></Box>
          <Box className={`${styles.particle} ${styles.particle3}`}></Box>
          <Box className={`${styles.particle} ${styles.particle4}`}></Box>
          <Box className={`${styles.particle} ${styles.particle5}`}></Box>
          <Box className={`${styles.particle} ${styles.particle6}`}></Box>

          {/* Floating App Icons */}
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon1}`}>
            📱
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon2}`}>
            💻
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon3}`}>
            🎯
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon4}`}>
            ⚡
          </Box>

          {/* Floating Shapes */}
          <Box className={`${styles.shape} ${styles.shape1}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape2}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
        </Box>

        <Container maxWidth="lg">
          <Box className={styles.heroContent}>
            <Typography
              variant="h1"
              className={styles.heroTitle}
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
              }}
            >
              Innovative <span className={styles.highlight}>Apps</span>
            </Typography>
            <Typography variant="h6" className={styles.heroSubtitle}>
              Customized and innovative solutions for your business needs
            </Typography>

            {/* Scroll Indicator */}
            <Box className={styles.scrollIndicator}>
              <Box className={styles.scrollMouse}>
                <Box className={styles.scrollWheel}></Box>
              </Box>
              <Typography variant="caption" className={styles.scrollText}>
                Scroll to explore
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Intro Section */}
      <Box className={styles.introSection}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Box className={styles.introContent}>
                <Typography
                  variant="h2"
                  className={styles.sectionTitle}
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
                  }}
                >
                  <span className={styles.highlight}>Wavelength/Wavepalm</span>{" "}
                  Innovative Apps
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We have successfully developed a range of{" "}
                  <strong>web and mobile apps</strong> for a range of sectors
                  including education, media, health, entertainment, finance,
                  leisure, sports and many more.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  Our key strength is the ability to develop{" "}
                  <strong>customized and innovative apps</strong> for your
                  business needs. You can also purchase some of our ready made
                  apps below.
                </Typography>
                <Box className={styles.sectorsGrid}>
                  {sectors.map((sector, index) => (
                    <Box key={index} className={styles.sectorTag}>
                      {sectorIcons[sector]}
                      <Typography variant="caption">{sector}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box className={styles.introImage}>
                <Paper elevation={0} className={styles.statsPaper}>
                  <Box className={styles.statItem}>
                    <Typography variant="h2" className={styles.statNumber}>
                      {appsData.length}+
                    </Typography>
                    <Typography variant="body2" className={styles.statLabel}>
                      Innovative Apps
                    </Typography>
                  </Box>
                  <Divider className={styles.statDivider} />
                  <Box className={styles.statItem}>
                    <Typography variant="h2" className={styles.statNumber}>
                      {sectors.length}
                    </Typography>
                    <Typography variant="body2" className={styles.statLabel}>
                      Industry Sectors
                    </Typography>
                  </Box>
                  <Divider className={styles.statDivider} />
                  <Box className={styles.statItem}>
                    <Typography variant="h2" className={styles.statNumber}>
                      4.8⭐
                    </Typography>
                    <Typography variant="body2" className={styles.statLabel}>
                      Average Rating
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Apps Showcase Section */}
      <Box className={styles.appsSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Ready Made <span className={styles.highlight}>Apps</span>
            </Typography>
            <Typography variant="body1" className={styles.sectionSubtitle}>
              Choose from our collection of innovative apps across various
              sectors
            </Typography>
          </Box>

          {/* Category Filters */}
          <Box className={styles.categoryFilters}>
            {categories.map((category) => (
              <button
                key={category.id}
                className={`${styles.categoryBtn} ${
                  selectedCategory === category.id
                    ? styles.categoryBtnActive
                    : ""
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className={styles.categoryIcon}>{category.icon}</span>
                {category.label}
              </button>
            ))}
          </Box>

          {/* Apps Grid */}
          <Grid container spacing={4}>
            {filteredApps.map((app) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={app.id}>
                <Card className={styles.appCard}>
                  <Box className={styles.cardImageWrapper}>
                    <CardMedia
                      component="img"
                      image={app.image}
                      alt={app.title}
                      className={styles.cardImage}
                    />
                    <Box className={styles.cardBadge}>
                      <Chip
                        label={app.sector}
                        size="small"
                        className={styles.sectorBadge}
                      />
                    </Box>
                    <Box className={styles.cardPrice}>
                      <Typography variant="h6" className={styles.priceText}>
                        {app.price}
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent className={styles.cardContent}>
                    <Box className={styles.cardHeader}>
                      <Box className={styles.cardIcon}>{app.icon}</Box>
                      <Typography variant="h6" className={styles.cardTitle}>
                        {app.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      className={styles.cardDescription}
                    >
                      {app.description}
                    </Typography>
                    <Box className={styles.cardFeatures}>
                      {app.features.slice(0, 3).map((feature, index) => (
                        <Box key={index} className={styles.featureChip}>
                          <CheckCircle className={styles.featureCheck} />
                          <Typography variant="caption">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box className={styles.cardFooter}>
                      <Box className={styles.cardRating}>
                        <Rating
                          value={app.rating}
                          precision={0.1}
                          readOnly
                          size="small"
                        />
                        <Typography
                          variant="caption"
                          className={styles.ratingText}
                        >
                          ({app.reviews})
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        className={styles.viewBtn}
                        onClick={() => handleOpenDialog(app)}
                        endIcon={<ArrowForward />}
                      >
                        View Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filteredApps.length === 0 && (
            <Box className={styles.noResults}>
              <Typography variant="h6">
                No apps found in this category
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Try selecting a different category
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* App Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        className={styles.dialog}
      >
        {selectedApp && (
          <>
            <DialogTitle className={styles.dialogTitle}>
              <Box className={styles.dialogHeader}>
                <Box className={styles.dialogIcon}>{selectedApp.icon}</Box>
                <Typography variant="h5" className={styles.dialogAppTitle}>
                  {selectedApp.title}
                </Typography>
                <IconButton
                  onClick={handleCloseDialog}
                  className={styles.dialogClose}
                >
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    component="img"
                    src={selectedApp.image}
                    alt={selectedApp.title}
                    className={styles.dialogImage}
                  />
                  <Box className={styles.dialogSector}>
                    <Chip
                      label={selectedApp.sector}
                      className={styles.dialogSectorChip}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    variant="body1"
                    className={styles.dialogDescription}
                  >
                    {selectedApp.description}
                  </Typography>
                  <Box className={styles.dialogFeatures}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Key Features:
                    </Typography>
                    {selectedApp.features.map((feature, index) => (
                      <Box key={index} className={styles.dialogFeature}>
                        <CheckCircle className={styles.dialogFeatureCheck} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box className={styles.dialogRating}>
                    <Rating
                      value={selectedApp.rating}
                      precision={0.1}
                      readOnly
                    />
                    <Typography
                      variant="body2"
                      className={styles.dialogRatingText}
                    >
                      {selectedApp.rating} ({selectedApp.reviews} reviews)
                    </Typography>
                  </Box>
                  <Box className={styles.dialogPrice}>
                    <Typography variant="h4" className={styles.dialogPriceText}>
                      {selectedApp.price}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
                className={styles.dialogCancelBtn}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className={styles.dialogBuyBtn}
                startIcon={<ShoppingCart />}
              >
                Purchase Now
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Custom Solutions CTA */}
      <Box className={styles.ctaSection}>
        <Container maxWidth="lg">
          <Box className={styles.ctaContent}>
            <Typography variant="h3" className={styles.ctaTitle}>
              Need a <span className={styles.highlight}>Custom</span> App?
            </Typography>
            <Typography variant="body1" className={styles.ctaText}>
              We specialize in developing customized and innovative apps
              tailored to your specific business needs. Let's create something
              amazing together.
            </Typography>
            <Box className={styles.ctaButtons}>
              <Button
                variant="contained"
                className={styles.ctaPrimaryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
              <Button
                variant="outlined"
                className={styles.ctaSecondaryBtn}
                startIcon={<Email />}
              >
                Email Us
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Innovation;
